export const DIPLOMACY_POINT_OPTIONS = [20, 30, 50] as const
// Relationship System v2: signed [-100,100] scale tier floors + request minimums (match backend).
export const POINTS_CEASEFIRE = 20
export const POINTS_ALLIANCE = 50
export const POINTS_MARRIAGE = 80
export const REQUEST_CEASEFIRE_MIN = 10
export const REQUEST_ALLIANCE_MIN = 30
export const REQUEST_MARRIAGE_MIN = 70

const D = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/land/diplomacy'

export const BG_URL = `${D}/background.webp`

/** Middle column header banner. */
export const DIPLOMACY_BANNER_URL = `${D}/banner.webp`

/** Incoming diplomacy empty message box — sealed envelope. */
export const DIPLOMACY_INBOX_URL = `${D}/inbox.webp`

/** Envoy idle panel — message / dispatch illustration (progression path when not sending). */
export const DIPLOMACY_NEW_MSG_URL = `${D}/new_msg.webp`

/** Envoy mission — send / recall CTAs and castle portraits (middle column). */
export const DIPLOMACY_BTN_ENVOY_URL = `${D}/btn_envoy.webp`
export const DIPLOMACY_BTN_RECALL_URL = `${D}/btn_recall.webp`
/** Diplomatic journey step icons (Sent → … → Response received). */
export const DIPLOMACY_JOURNEY_STAGE_URLS = [
  `${D}/stage1.webp`,
  `${D}/stage2.webp`,
  `${D}/stage3.webp`,
  `${D}/stage4.webp`,
  `${D}/stage5.webp`,
] as const
/** Middle march row — source castle (sent) + target castle on active progression panel. */
export const DIPLOMACY_ENVOY_STAGE_SELF_URL = DIPLOMACY_JOURNEY_STAGE_URLS[0]
/** Target ruler castle art — `stage3.webp` (arrived / their seat). */
export const DIPLOMACY_ENVOY_STAGE_TARGET_URL = DIPLOMACY_JOURNEY_STAGE_URLS[2]
/** Rider on path — outbound leg (toward target). */
export const DIPLOMACY_ENVOY_HORSE_URL = `${D}/horse.webp`
/** Return leg (toward home). */
export const DIPLOMACY_ENVOY_HORSE_BACK_URL = `${D}/horse_back.webp`
/** Live envoy march panel background (in-progress mission view). */
export const DIPLOMACY_ENVOY_MISSION_BG_URL = `${D}/live_mission_background.webp`
/** Must match server `DIPLOMACY_RESOURCE_PER_POINT.bread` (bread units per 1 diplomacy point). */
export const DIPLOMACY_RESOURCE_BREAD_PER_POINT = 112

/** Decorative frames for the three-column diplomacy layout (short | long | short). */
export const SHORT_FRAME_URL = `${D}/short_frame.webp`
export const LONG_FRAME_URL = `${D}/long_frame.webp`

/** Relationship stepper (spirit → marriage) status artwork. */
export const DIPLOMACY_STATUS_SPIRIT_URL = `${D}/status_spirit.webp`
export const DIPLOMACY_STATUS_CEASEFIRE_URL = `${D}/status_ceasefire.webp`
export const DIPLOMACY_STATUS_ALLY_URL = `${D}/status_ally.webp`
export const DIPLOMACY_STATUS_MARRIAGE_URL = `${D}/status_marriage.webp`

// --- Relationship System v2: signed score [-100,100], 1 decimal, 7 named tiers ---
export type RelationshipTier =
  | 'nemesis'
  | 'hostile'
  | 'wary'
  | 'cordial'
  | 'ceasefire'
  | 'alliance'
  | 'marriage'

/** Tier floors on the signed scale (must match backend diplomacyRelationshipScore). */
export const RELATIONSHIP_TIER_FLOORS: Record<RelationshipTier, number> = {
  nemesis: -100,
  hostile: -50,
  wary: -20,
  cordial: 0,
  ceasefire: 20,
  alliance: 50,
  marriage: 80,
}

/** Derive the tier name from a signed relationship score. */
export function scoreTier(score: number): RelationshipTier {
  const s = Number(score) || 0
  if (s >= RELATIONSHIP_TIER_FLOORS.marriage) return 'marriage'
  if (s >= RELATIONSHIP_TIER_FLOORS.alliance) return 'alliance'
  if (s >= RELATIONSHIP_TIER_FLOORS.ceasefire) return 'ceasefire'
  if (s >= RELATIONSHIP_TIER_FLOORS.cordial) return 'cordial'
  if (s >= RELATIONSHIP_TIER_FLOORS.wary) return 'wary'
  if (s >= RELATIONSHIP_TIER_FLOORS.hostile) return 'hostile'
  return 'nemesis'
}

/** Signed score for display: floor to 1 decimal, clamp to [-100,100], as a fixed 1-decimal string. */
export function formatScore(score: number): string {
  const s = Math.max(-100, Math.min(100, Math.floor((Number(score) || 0) * 10) / 10))
  return s.toFixed(1)
}

/** Tier → i18n key: t(`diplomacy.${TIER_I18N_KEY[tier]}`). */
export const TIER_I18N_KEY: Record<RelationshipTier, string> = {
  nemesis: 'nemesis',
  hostile: 'hostile',
  wary: 'wary',
  cordial: 'cordial',
  ceasefire: 'ceasefire',
  alliance: 'alliance',
  marriage: 'marriage',
}

/** Tier → stepper status artwork. Negative tiers reuse the neutral art until dedicated assets exist. */
export const TIER_STATUS_ART: Record<RelationshipTier, string> = {
  nemesis: DIPLOMACY_STATUS_SPIRIT_URL,
  hostile: DIPLOMACY_STATUS_SPIRIT_URL,
  wary: DIPLOMACY_STATUS_SPIRIT_URL,
  cordial: DIPLOMACY_STATUS_SPIRIT_URL,
  ceasefire: DIPLOMACY_STATUS_CEASEFIRE_URL,
  alliance: DIPLOMACY_STATUS_ALLY_URL,
  marriage: DIPLOMACY_STATUS_MARRIAGE_URL,
}

/** Search Player / World Map tab toggle images. */
export const DIPLOMACY_TAB_BTN_ACTIVE_URL = `${D}/btn_active.webp`
export const DIPLOMACY_TAB_BTN_DEACTIVE_URL = `${D}/btn_deactive.webp`

/** View profile CTA on banner strip (middle column). */
export const DIPLOMACY_BTN_CONFIRM_URL = `${D}/btn_confirm.webp`
/** Incoming diplomacy — reject envoy (matches parchment panel CTAs). */
export const DIPLOMACY_BTN_REQUEST_URL = `${D}/btn_request.webp`

export const DIPLOMACY_SHAKE_HANDS_URL = `${D}/shake_hands.webp`

export const DIPLOMACY_BTN_REFRESH_URL = `${D}/btn_refresh.webp`
export const DIPLOMACY_BTN_SEARCH_URL = `${D}/btn_search.webp`
export const DIPLOMACY_INPUT_BAR_URL = `${D}/input_bar.webp`

/** Gift send modal — increment resource row amount. */
export const DIPLOMACY_GIFT_BTN_PLUS_URL = `${D}/btn_plus.webp`
/** Gift send modal — plate behind numeric amount field. */
export const DIPLOMACY_GIFT_AMOUNT_PANEL_URL = `${D}/panel.webp`

export const DIPLOMACY_USER_CARD_ACTIVE_URL = `${D}/active_user_card.webp`
export const DIPLOMACY_USER_CARD_DEACTIVE_URL = `${D}/deactive_user_card.webp`
export const DIPLOMACY_USER_AVATAR_FRAME_URL = `${D}/user_avatar_frame.webp`
export const DIPLOMACY_USER_AVATAR_FRAME_ACTIVE_URL = `${D}/user_avatar_frame_active.webp`
export const TITLE_BASE_URL = `${D}/title.webp`
export const LEFT_PANEL_BG = `${D}/left-panel-bg.webp`
export const RIGHT_PANEL_BG = `${D}/right-panel-bg.webp`
export const BUTTON_BG = `${D}/button-bg.webp`
/** Diplomacy character panel — Start training CTA. */
export const DIPLOMACY_TRAINING_BTN_BG = `${D}/btn_training.webp`
export const BREAK_RELATIONSHIP_BTN_BG = `${D}/break-relationship-btn-bg.webp`
export const DEFAULT_AVATAR_URL = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/_merged_/NFT/default.webp'
export const BREAD_ICON = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/land/bread-icon.webp'
export const ECS_ICON = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/eclipsera-gem.webp'
export const DIPLOMACY_UPGRADE_COST_BREAD = 50
export const DIPLOMACY_UPGRADE_COST_ECS = 2
/** Training score cap for diplomacy skill UI. */
export const DIPLOMACY_MAX_TRAINING_SCORE = 30
/** Points shown as gained per training session (must match game rules). */
export const DIPLOMACY_TRAINING_SCORE_INCREMENT = 10

export const DRAG_THRESHOLD = 6

export const GIFT_RESOURCES = ['ecs', 'water', 'wheat', 'bread', 'wood', 'stone', 'iron'] as const
export type GiftResourceType = (typeof GIFT_RESOURCES)[number]
export const GIFT_RESOURCE_ICONS: Record<string, string> = {
  water: 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/_merged_/Eclipsera/water_reward_icon.webp',
  ecs: 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/eclipsera-gem.webp',
  wheat: 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/land/wheat-icon.webp',
  bread: 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/land/bread-icon.webp',
  wood: 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/land/wood-icon.webp',
  stone: 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/land/stone-icon.webp',
  iron: 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/land/iron-icon.webp',
}

/** ECS-equivalent diplomacy points from selected resources (matches server `DIPLOMACY_RESOURCE_PER_POINT`). */
export const DIPLOMACY_RESOURCE_PER_POINT: Record<GiftResourceType, number> = {
  ecs: 1,
  water: 25,
  wheat: 280,
  bread: 112,
  wood: 112,
  stone: 56,
  iron: 28,
}

export function diplomacyGiftTotalPoints(amounts: Partial<Record<GiftResourceType, number>>): number {
  let t = 0
  for (const k of GIFT_RESOURCES) {
    const a = Math.floor(Number(amounts[k]) || 0)
    t += a / DIPLOMACY_RESOURCE_PER_POINT[k]
  }
  return t
}

export function emptyGiftAmounts(): Record<GiftResourceType, number> {
  return Object.fromEntries(GIFT_RESOURCES.map((k) => [k, 0])) as Record<GiftResourceType, number>
}

/** Inventory snapshot for diplomacy envoy gift UI — same keys as `GIFT_RESOURCES`. */
export type DiplomacyPlayerBalances = Record<GiftResourceType, number>

export const SLIDER_TRACK_HEIGHT = 5
export const SLIDER_THUMB_WIDTH = 12
export const SLIDER_THUMB_HEIGHT = 14
export const SLIDER_HEIGHT = 20

export const getTitleUrlByLang = (lang: string): string => {
  if (!lang || lang === 'en') return TITLE_BASE_URL
  const base = lang.split('-')[0]
  const supported = ['ar', 'ja', 'ru', 'zh']
  if (!supported.includes(base)) return TITLE_BASE_URL
  return TITLE_BASE_URL.replace(/\.webp$/, `-${base}.webp`)
}

/**
 * Kingdom diplomacy UI — keep in sync with `VERCEL_BLOB_PRELOAD_PRIORITIZED` via spread in `vercelBlobPreloadUrls.ts`.
 * Includes localized title assets used by `getTitleUrlByLang`.
 */
export const DIPLOMACY_PRELOAD_URLS: readonly string[] = [
  BG_URL,
  DIPLOMACY_BANNER_URL,
  DIPLOMACY_INBOX_URL,
  DIPLOMACY_NEW_MSG_URL,
  DIPLOMACY_BTN_ENVOY_URL,
  DIPLOMACY_BTN_RECALL_URL,
  ...DIPLOMACY_JOURNEY_STAGE_URLS,
  DIPLOMACY_ENVOY_HORSE_URL,
  DIPLOMACY_ENVOY_HORSE_BACK_URL,
  DIPLOMACY_ENVOY_MISSION_BG_URL,
  SHORT_FRAME_URL,
  LONG_FRAME_URL,
  DIPLOMACY_STATUS_SPIRIT_URL,
  DIPLOMACY_STATUS_CEASEFIRE_URL,
  DIPLOMACY_STATUS_ALLY_URL,
  DIPLOMACY_STATUS_MARRIAGE_URL,
  DIPLOMACY_TAB_BTN_ACTIVE_URL,
  DIPLOMACY_TAB_BTN_DEACTIVE_URL,
  DIPLOMACY_BTN_CONFIRM_URL,
  DIPLOMACY_BTN_REQUEST_URL,
  DIPLOMACY_SHAKE_HANDS_URL,
  DIPLOMACY_BTN_REFRESH_URL,
  DIPLOMACY_BTN_SEARCH_URL,
  DIPLOMACY_INPUT_BAR_URL,
  DIPLOMACY_GIFT_BTN_PLUS_URL,
  DIPLOMACY_GIFT_AMOUNT_PANEL_URL,
  DIPLOMACY_USER_CARD_ACTIVE_URL,
  // DIPLOMACY_USER_CARD_DEACTIVE_URL,
  DIPLOMACY_USER_AVATAR_FRAME_URL,
  DIPLOMACY_USER_AVATAR_FRAME_ACTIVE_URL,
  // TITLE_BASE_URL,
  // LEFT_PANEL_BG,
  // RIGHT_PANEL_BG,
  // BUTTON_BG,
  DIPLOMACY_TRAINING_BTN_BG,
  // BREAK_RELATIONSHIP_BTN_BG,
]
